/**
 * PDFViewer Component - PDF listing and preview using left-right layout
 * Similar to BRD and User Manual components with independent scrolling sections
 */

import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import FormControl from '../common/FormControl';

interface PDFDocument {
  id: string;
  name: string;
  path: string;
  description: string;
  category: string;
  size?: string;
  uploadDate?: string;
}

interface PDFCategory {
  id: string;
  name: string;
  icon: string;
  files: PDFDocument[];
}

const PDFViewer: React.FC = () => {
  // State management
  const [categories, setCategories] = useState<PDFCategory[]>([]);
  const [selectedFile, setSelectedFile] = useState<PDFDocument | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [contentLoading, setContentLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // PDF Documents from JSON data
  const pdfDocuments: PDFDocument[] = [
    {
      id: 'workflow-approval-md',
      name: 'Document Approval Workflow (Styled)',
      path: '/pdfs/workflows/document-approval.md',
      description: 'Comprehensive document approval workflow with styled markdown formatting, tables, and status indicators',
      category: 'workflows',
      size: '1.5 MB',
      uploadDate: '2024-10-05'
    },
    {
      id: 'workflow-approval',
      name: 'Approval Workflow Process (HTML)',
      path: '/pdfs/workflows/approval-workflow.html',
      description: 'Step-by-step guide for document approval processes and maker-checker workflows',
      category: 'workflows',
      size: '2.3 MB',
      uploadDate: '2024-10-01'
    },
    {
      id: 'policy-privacy-md',
      name: 'Data Privacy Policy (Styled)',
      path: '/pdfs/policies/data-privacy.md',
      description: 'Comprehensive data privacy policy with GDPR compliance, formatted with markdown styling and tables',
      category: 'policies',
      size: '3.1 MB',
      uploadDate: '2024-10-05'
    },
    {
      id: 'policy-security',
      name: 'Information Security Policy (HTML)',
      path: '/pdfs/policies/information-security.html',
      description: 'Comprehensive information security policy covering data protection, access controls, and incident management',
      category: 'policies',
      size: '4.2 MB',
      uploadDate: '2024-09-20'
    },
    {
      id: 'workflow-compliance',
      name: 'Compliance Review Workflow',
      path: 'https://mozilla.github.io/pdf.js/web/compressed.tracemonkey-pldi-09.pdf',
      description: 'Detailed workflow for compliance reviews, audit trails, and regulatory submissions (Sample PDF)',
      category: 'workflows',
      size: '1.8 MB',
      uploadDate: '2024-09-28'
    },
    {
      id: 'policy-data-governance',
      name: 'Data Governance Policy',
      path: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
      description: 'Data governance framework including data quality, privacy, and lifecycle management policies (Sample PDF)',
      category: 'policies',
      size: '2.7 MB',
      uploadDate: '2024-09-15'
    },
    {
      id: 'process-risk',
      name: 'Risk Management Process',
      path: '/pdfs/processes/risk-management.html',
      description: 'Enterprise risk management framework including identification, assessment, and mitigation strategies',
      category: 'processes',
      size: '2.9 MB',
      uploadDate: '2024-08-30'
    },
    {
      id: 'process-audit',
      name: 'Internal Audit Process',
      path: 'https://www.africau.edu/images/default/sample.pdf',
      description: 'Internal audit methodology, risk assessment procedures, and reporting standards (Sample PDF)',
      category: 'processes',
      size: '3.5 MB',
      uploadDate: '2024-09-05'
    }
  ];

  // Organize documents into categories
  useEffect(() => {
    const categoriesMap: { [key: string]: PDFCategory } = {
      workflows: { id: 'workflows', name: 'Workflows', icon: '🔄', files: [] },
      policies: { id: 'policies', name: 'Policies', icon: '📋', files: [] },
      processes: { id: 'processes', name: 'Processes', icon: '⚙️', files: [] },
      templates: { id: 'templates', name: 'Templates', icon: '📄', files: [] },
      guidelines: { id: 'guidelines', name: 'Guidelines', icon: '📖', files: [] }
    };

    pdfDocuments.forEach(doc => {
      if (categoriesMap[doc.category]) {
        categoriesMap[doc.category].files.push(doc);
      }
    });

    const categoriesList = Object.values(categoriesMap).filter(cat => cat.files.length > 0);
    setCategories(categoriesList);

    // Auto-expand first category and select first file
    if (categoriesList.length > 0) {
      const firstCategory = categoriesList[0];
      setExpandedCategories(new Set([firstCategory.id]));
      
      if (firstCategory.files.length > 0) {
        handleFileSelect(firstCategory.files[0]);
      }
    }
  }, []);

  const handleFileSelect = async (file: PDFDocument) => {
    setSelectedFile(file);
    setError(null);
    
    // Check if file is markdown or HTML for content fetching
    if (file.path.endsWith('.md') || file.path.endsWith('.html')) {
      setContentLoading(true);
      try {
        const response = await fetch(file.path);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }
        const content = await response.text();
        setFileContent(content);
      } catch (error: any) {
        console.error('Error loading file:', error);
        setError(error.message || 'Failed to load document');
        setFileContent('');
      } finally {
        setContentLoading(false);
      }
    } else {
      setFileContent('');
    }
  };

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const getFilteredFiles = (): PDFDocument[] => {
    if (!searchTerm.trim()) return [];
    return pdfDocuments.filter(doc =>
      doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredFiles = getFilteredFiles();
  const showSearchResults = searchTerm.trim().length > 0;

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <p>Loading PDF documents...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Main Content Layout - Fixed Height Container */}
      <div className="main-content-area">
        {/* Left Sidebar - PDF Categories (25%) with SCROLL */}
        <div className="left_sidebar">
          {/* Search Box - Fixed at top */}
          <div style={{
            padding: 'var(--spacing-md)',
            background: 'var(--white)',
            borderBottom: '1px solid var(--gray-200)',
            flexShrink: 0
          }}>
            <div style={{ position: 'relative' }}>
              <FormControl.Input
                placeholder="Search PDF documents..."
                value={searchTerm}
                onChange={handleSearch}
                icon={<span style={{ color: 'var(--gray-500)' }}>🔍</span>}
                data-testid="INP_pdf_viewer_search"
              />
            </div>
          </div>

          {/* Scrollable Content Area for Categories/Search Results */}
          <div style={{ 
            flex: 1, 
            overflowY: 'auto', 
            padding: '0' 
          }}>
            {/* Search Results */}
            {showSearchResults && (
              <div style={{ padding: '0 var(--spacing-md)' }}>
                <div style={{ 
                  padding: 'var(--spacing-md) 0',
                  borderBottom: '1px solid var(--gray-200)',
                  background: 'var(--white)'
                }}>
                  <h3 style={{ 
                    margin: '0',
                    fontSize: 'var(--font-size-base)',
                    fontWeight: '600',
                    color: 'var(--gray-800)'
                  }}>Search Results ({filteredFiles.length})</h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                  {filteredFiles.length === 0 ? (
                    <div className="empty-state">
                      <p>No documents found for "{searchTerm}"</p>
                    </div>
                  ) : (
                    filteredFiles.map(file => (
                      <div
                        key={file.id}
                        data-testid={`NAV_pdf_viewer_search_result_${file.id}`}
                        style={{
                          padding: 'var(--spacing-md)',
                          background: selectedFile?.id === file.id ? 'var(--primary-blue-light)' : 'var(--white)',
                          border: `1px solid ${selectedFile?.id === file.id ? 'var(--primary-blue)' : 'var(--gray-200)'}`,
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => handleFileSelect(file)}
                      >
                        <div style={{ fontWeight: '600', color: 'var(--gray-800)' }}>{file.name}</div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)', marginBottom: 'var(--spacing-xs)' }}>
                          {file.size} • {new Date(file.uploadDate || '').toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)', textTransform: 'capitalize' }}>
                          {file.category}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Categories List */}
            {!showSearchResults && (
              <div>
                <div style={{ 
                  padding: 'var(--spacing-md)',
                  background: 'var(--white)',
                  borderBottom: '1px solid var(--gray-200)'
                }}>
                  <h3 style={{ 
                    margin: '0',
                    fontSize: 'var(--font-size-base)',
                    fontWeight: '600',
                    color: 'var(--gray-800)'
                  }}>PDF Categories</h3>
                </div>
                
                {categories.map(category => (
                  <div key={category.id} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                    <div
                      data-testid={`NAV_pdf_viewer_category_${category.id}`}
                      style={{
                        padding: 'var(--spacing-md)',
                        background: 'var(--white)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-sm)',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={() => toggleCategory(category.id)}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gray-50)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--white)'}
                    >
                      <span style={{ fontSize: 'var(--font-size-lg)', width: '24px', textAlign: 'center' }}>
                        {category.icon}
                      </span>
                      <span style={{ 
                        flex: 1, 
                        fontWeight: '500', 
                        color: 'var(--gray-800)' 
                      }}>
                        {category.name}
                      </span>
                      <span style={{ 
                        fontSize: 'var(--font-size-xs)', 
                        color: 'var(--gray-600)' 
                      }}>
                        ({category.files.length})
                      </span>
                      <span style={{ 
                        fontSize: 'var(--font-size-xs)',
                        transition: 'transform 0.2s',
                        transform: expandedCategories.has(category.id) ? 'rotate(90deg)' : 'rotate(0deg)',
                        color: 'var(--gray-600)'
                      }}>
                        ▶
                      </span>
                    </div>
                    
                    {expandedCategories.has(category.id) && (
                      <div style={{ background: 'var(--gray-50)' }}>
                        {category.files.map(file => (
                          <div
                            key={file.id}
                            data-testid={`NAV_pdf_viewer_file_${file.id}`}
                            style={{
                              padding: 'var(--spacing-md) var(--spacing-md) var(--spacing-md) var(--spacing-xxl)',
                              borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              background: selectedFile?.id === file.id ? 'var(--primary-blue-light)' : 'transparent',
                              borderLeft: selectedFile?.id === file.id ? '3px solid var(--primary-blue)' : '3px solid transparent'
                            }}
                            onClick={() => handleFileSelect(file)}
                            onMouseEnter={(e) => {
                              if (selectedFile?.id !== file.id) {
                                e.currentTarget.style.background = 'rgba(0, 123, 255, 0.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedFile?.id !== file.id) {
                                e.currentTarget.style.background = 'transparent';
                              }
                            }}
                            title={file.description}
                          >
                            <div style={{ 
                              fontWeight: '500',
                              color: 'var(--gray-800)',
                              fontSize: 'var(--font-size-sm)',
                              marginBottom: 'var(--spacing-xs)'
                            }}>
                              📄 {file.name}
                            </div>
                            <div style={{ 
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--gray-600)',
                              marginBottom: 'var(--spacing-xs)'
                            }}>
                              {file.size} • {new Date(file.uploadDate || '').toLocaleDateString()}
                            </div>
                            <div className="data_default" style={{ 
                              fontSize: 'var(--font-size-xs)',
                              lineHeight: '1.4'
                            }}>
                              {file.description}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - PDF Viewer (75%) with SCROLL */}
        <div className="mid_content_area">
          {selectedFile ? (
            <>
              {/* File Name Header - Fixed at top */}
              <div style={{
                padding: 'var(--spacing-lg)',
                borderBottom: '1px solid var(--gray-200)',
                background: 'var(--white)',
                flexShrink: 0
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <h3 style={{
                      margin: '0 0 var(--spacing-xs) 0',
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: '700',
                      color: 'var(--gray-800)'
                    }}>
                      📄 {selectedFile.name}
                    </h3>
                    <div style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--gray-600)'
                    }}>
                      {selectedFile.size} • Uploaded {new Date(selectedFile.uploadDate || '').toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                    <button
                      style={{
                        padding: 'var(--spacing-sm) var(--spacing-md)',
                        background: 'var(--primary-blue)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-sm)'
                      }}
                      onClick={() => window.open(selectedFile.path, '_blank')}
                      data-testid="BTN_pdf_viewer_download"
                    >
                      Download 📥
                    </button>
                  </div>
                </div>
              </div>

              {/* Document Content - Scrollable */}
              <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                padding: '0',
                background: '#f5f5f5'
              }}>
                {contentLoading ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading document content...</p>
                  </div>
                ) : error ? (
                  <div className="empty-state">
                    <div className="empty-state-content">
                      <div className="empty-state-icon">⚠️</div>
                      <h3>Error Loading Document</h3>
                      <p>{error}</p>
                      <button
                        className="add-btn-primary"
                        onClick={() => window.open(selectedFile.path, '_blank')}
                        data-testid="BTN_pdf_viewer_open_new_tab"
                      >
                        Open in New Tab
                      </button>
                    </div>
                  </div>
                ) : selectedFile.path.endsWith('.md') ? (
                  // Markdown Content with Styling
                  <div style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    padding: 'var(--spacing-lg)'
                  }}>
                    <div style={{
                      width: '100%',
                      maxWidth: '900px',
                      background: 'white',
                      borderRadius: 'var(--radius-lg)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      padding: 'var(--spacing-xxl)',
                      lineHeight: '1.6'
                    }}>
                      <ReactMarkdown
                        components={{
                          h1: ({children}) => (
                            <h1 style={{
                              color: '#ff6b35',
                              borderBottom: '2px solid #ff6b35',
                              paddingBottom: '10px',
                              marginBottom: '20px',
                              fontSize: '2rem'
                            }}>{children}</h1>
                          ),
                          h2: ({children}) => (
                            <h2 style={{
                              color: '#333',
                              borderBottom: '1px solid #eee',
                              paddingBottom: '8px',
                              marginTop: '30px',
                              marginBottom: '16px',
                              fontSize: '1.5rem'
                            }}>{children}</h2>
                          ),
                          h3: ({children}) => (
                            <h3 style={{
                              color: '#444',
                              marginTop: '24px',
                              marginBottom: '12px',
                              fontSize: '1.25rem'
                            }}>{children}</h3>
                          ),
                          table: ({children}) => (
                            <table style={{
                              borderCollapse: 'collapse',
                              width: '100%',
                              margin: '20px 0',
                              border: '1px solid #ddd'
                            }}>{children}</table>
                          ),
                          th: ({children}) => (
                            <th style={{
                              border: '1px solid #ddd',
                              padding: '12px',
                              backgroundColor: '#f8f9fa',
                              fontWeight: '600',
                              textAlign: 'left'
                            }}>{children}</th>
                          ),
                          td: ({children}) => (
                            <td style={{
                              border: '1px solid #ddd',
                              padding: '12px'
                            }}>{children}</td>
                          ),
                          blockquote: ({children}) => (
                            <blockquote style={{
                              borderLeft: '4px solid #ff6b35',
                              paddingLeft: '16px',
                              margin: '16px 0',
                              fontStyle: 'italic',
                              background: '#f8f9fa',
                              padding: '12px 16px'
                            }}>{children}</blockquote>
                          ),
                          code: ({children, className}) => (
                            className ? (
                              <pre style={{
                                background: '#f8f9fa',
                                padding: '16px',
                                borderRadius: '6px',
                                overflow: 'auto',
                                border: '1px solid #e9ecef',
                                margin: '16px 0'
                              }}>
                                <code style={{
                                  fontFamily: "'Fira Code', Monaco, Consolas, monospace",
                                  fontSize: '14px'
                                }}>{children}</code>
                              </pre>
                            ) : (
                              <code style={{
                                background: '#f1f3f4',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontFamily: "'Fira Code', Monaco, Consolas, monospace",
                                fontSize: '14px'
                              }}>{children}</code>
                            )
                          ),
                          ul: ({children}) => (
                            <ul style={{
                              paddingLeft: '20px',
                              margin: '12px 0'
                            }}>{children}</ul>
                          ),
                          ol: ({children}) => (
                            <ol style={{
                              paddingLeft: '20px',
                              margin: '12px 0'
                            }}>{children}</ol>
                          ),
                          li: ({children}) => (
                            <li style={{
                              marginBottom: '6px'
                            }}>{children}</li>
                          ),
                          strong: ({children}) => (
                            <strong style={{
                              color: '#2c3e50',
                              fontWeight: '600'
                            }}>{children}</strong>
                          ),
                          hr: () => (
                            <hr style={{
                              border: 'none',
                              borderTop: '2px solid #e9ecef',
                              margin: '30px 0'
                            }} />
                          )
                        }}
                      >
                        {fileContent}
                      </ReactMarkdown>
                    </div>
                  </div>
                ) : selectedFile.path.endsWith('.html') ? (
                  // HTML Content with iframe
                  <div style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '800px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    padding: 'var(--spacing-lg)'
                  }}>
                    <div style={{
                      width: '100%',
                      maxWidth: '900px',
                      background: 'white',
                      borderRadius: 'var(--radius-lg)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      overflow: 'hidden'
                    }}>
                      <iframe
                        src={selectedFile.path}
                        style={{
                          width: '100%',
                          height: '800px',
                          border: 'none'
                        }}
                        title={selectedFile.name}
                        onError={() => setError('Unable to load document. File may not exist.')}
                      />
                    </div>
                  </div>
                ) : (
                  // PDF Content with iframe
                  <div style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '800px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    padding: 'var(--spacing-lg)'
                  }}>
                    <div style={{
                      width: '100%',
                      maxWidth: '900px',
                      background: 'white',
                      borderRadius: 'var(--radius-lg)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      overflow: 'hidden'
                    }}>
                      <iframe
                        src={selectedFile.path}
                        style={{
                          width: '100%',
                          height: '800px',
                          border: 'none'
                        }}
                        title={selectedFile.name}
                        onError={() => setError('Unable to load PDF. File may not exist.')}
                      >
                        <div style={{
                          padding: 'var(--spacing-xxl)',
                          textAlign: 'center',
                          color: 'var(--gray-600)'
                        }}>
                          <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-lg)' }}>📄</div>
                          <h3>PDF Preview Not Available</h3>
                          <p>Your browser doesn't support PDF preview.</p>
                          <button
                            style={{
                              padding: 'var(--spacing-md) var(--spacing-lg)',
                              background: 'var(--primary-blue)',
                              color: 'white',
                              border: 'none',
                              borderRadius: 'var(--radius-md)',
                              cursor: 'pointer',
                              fontSize: 'var(--font-size-base)',
                              marginTop: 'var(--spacing-md)'
                            }}
                            onClick={() => window.open(selectedFile.path, '_blank')}
                            data-testid="BTN_pdf_viewer_pdf_fallback_open"
                          >
                            Open PDF in New Tab
                          </button>
                        </div>
                      </iframe>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-state-content">
                <div className="empty-state-icon">📄</div>
                <h3>Select a PDF Document</h3>
                <p>Choose a PDF document from the categories on the left to view its content.</p>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 'var(--spacing-lg)',
                  marginTop: 'var(--spacing-lg)'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontSize: 'var(--font-size-xl)',
                      color: 'var(--primary-blue)',
                      fontWeight: '600',
                      marginBottom: 'var(--spacing-xs)'
                    }}>
                      {categories.length}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-sm)' }}>Categories</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontSize: 'var(--font-size-xl)',
                      color: 'var(--primary-blue)',
                      fontWeight: '600',
                      marginBottom: 'var(--spacing-xs)'
                    }}>
                      {pdfDocuments.length}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-sm)' }}>Total Documents</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;